import type {
  CRMVendor,
  IOrganizationData,
  Integration,
  IntegrationData,
  IntegrationVendor,
  IntegrationsConfig,
  OpportunityFilter,
} from '@carboncopyco/types';
import { removeUndefinedValues } from '@carboncopyco/utils';
import { snakeCase } from 'snake-case';
import { mainApi } from './mainApi';

interface GetIntegrationsArgs {
  organizationId: IOrganizationData['id'];
}

interface GetIntegrationDataArgs {
  organizationId: IOrganizationData['id'];
  integrationId: Integration['id'];
  filter?: OpportunityFilter;
  search?: string;
}

interface UpdateIntegrationArgs {
  organizationId: IOrganizationData['id'];
  integrationId: Integration['id'];
  paused: boolean;
}

interface DeleteIntegrationArgs {
  organizationId: IOrganizationData['id'];
  integrationId: Integration['id'];
}

interface RenewIntegrationLinkArgs {
  organizationId: IOrganizationData['id'];
  integrationId: Integration['id'];
}

interface RenewIntegrationLinkResponse {
  integrationReconnectUrl: string;
  integrationType: IntegrationVendor;
}

interface CreateIntegrationLinkArgs {
  organizationId: IOrganizationData['id'];
  integrationType: IntegrationVendor;
  isSandbox?: boolean;
}

interface CreateIntegrationLinkResponse {
  integrationUrl: string;
  integrationType: IntegrationVendor;
}

type GetIntegrationsConfigArgs = { organizationId: string };

type UpdateIntegrationsConfigArgs = {
  organizationId: string;
  crm: CRMVendor | null;
};
export const integrationsApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    getIntegrationsConfig: builder.query<
      IntegrationsConfig,
      GetIntegrationsConfigArgs
    >({
      query: ({ organizationId }) => ({
        url: `/api/${organizationId}/settings/integrations/config`,
        method: 'GET',
      }),
      providesTags: ['IntegrationsConfig'],
    }),
    updateIntegrationsConfig: builder.mutation<
      IntegrationsConfig,
      UpdateIntegrationsConfigArgs
    >({
      query: ({ organizationId, crm }) => ({
        url: `/api/${organizationId}/settings/integrations/config`,
        method: 'POST',
        body: { crm },
      }),
      invalidatesTags: ['IntegrationsConfig'],
      onQueryStarted: async (
        { organizationId, crm },
        { dispatch, queryFulfilled, getState }
      ) => {
        const patchResult = dispatch(
          integrationsApi.util.updateQueryData(
            'getIntegrationsConfig',
            { organizationId },
            (draftConfig) => ({ ...draftConfig, crm })
          )
        );
        queryFulfilled.catch(patchResult.undo);
      },
    }),
    getIntegrations: builder.query<Integration[], GetIntegrationsArgs>({
      query: ({ organizationId }) => ({
        method: 'GET',
        url: `/api/${organizationId}/settings/integrations`,
      }),
      providesTags: ['Integrations'],
    }),
    getIntegrationData: builder.query<IntegrationData, GetIntegrationDataArgs>({
      query: ({ organizationId, integrationId, filter, search }) => ({
        method: 'GET',
        url: `/api/${organizationId}/integration/${integrationId}/query`,
        params: removeUndefinedValues({
          query: snakeCase(filter || 'activeOpportunities'),
          search_keyword: search,
        }),
      }),
      providesTags: (_, __, { integrationId }) => [
        { type: 'Integration', id: integrationId },
      ],
    }),
    updateIntegration: builder.mutation<Integration, UpdateIntegrationArgs>({
      query: ({ organizationId, integrationId, paused }) => ({
        method: 'POST',
        url: `/api/${organizationId}/settings/integration/${integrationId}`,
        body: { paused },
      }),
      onQueryStarted: async (
        { organizationId, integrationId, paused },
        { dispatch, queryFulfilled }
      ) => {
        const patchResult = dispatch(
          integrationsApi.util.updateQueryData(
            'getIntegrations',
            { organizationId },
            (draftIntegrations) => {
              const integrationIndexToUpdate = draftIntegrations.findIndex(
                (integration) => integration.id === integrationId
              );
              const updatedIntegration = {
                ...draftIntegrations[integrationIndexToUpdate],
                paused,
              };
              draftIntegrations.splice(
                integrationIndexToUpdate,
                1,
                updatedIntegration
              );
              return draftIntegrations;
            }
          )
        );
        queryFulfilled.catch(patchResult.undo);
      },
      invalidatesTags: (_, __, { integrationId }) => [
        { type: 'Integration', id: integrationId },
        { type: 'Integrations' },
      ],
    }),
    deleteIntegration: builder.mutation<Integration, DeleteIntegrationArgs>({
      query: ({ organizationId, integrationId }) => ({
        method: 'DELETE',
        url: `/api/${organizationId}/settings/integration/${integrationId}`,
        body: removeUndefinedValues({}),
      }),
      onQueryStarted: async (
        { organizationId, integrationId },
        { dispatch, queryFulfilled }
      ) => {
        const patchResult = dispatch(
          integrationsApi.util.updateQueryData(
            'getIntegrations',
            { organizationId },
            (draftIntegrations) =>
              draftIntegrations.filter(
                (integration) => integration.id !== integrationId
              )
          )
        );
        queryFulfilled.catch(patchResult.undo);
      },
      invalidatesTags: (_, __, { integrationId }) => [
        { type: 'Integration', id: integrationId },
        { type: 'Integrations' },
      ],
    }),
    createIntegrationLink: builder.mutation<
      CreateIntegrationLinkResponse,
      CreateIntegrationLinkArgs
    >({
      query: ({ organizationId, integrationType, isSandbox }) => ({
        method: 'POST',
        url: `/api/${organizationId}/settings/integration/${integrationType}/creation-link`,
        body: removeUndefinedValues({ isSandbox }),
      }),
    }),
    renewIntegrationLink: builder.mutation<
      RenewIntegrationLinkResponse,
      RenewIntegrationLinkArgs
    >({
      query: ({ organizationId, integrationId }) => ({
        method: 'POST',
        url: `/api/${organizationId}/settings/integration/${integrationId}/renewal-link`,
      }),
      invalidatesTags: (_, __, { integrationId }) => [
        { type: 'Integration', id: integrationId },
      ],
    }),
  }),
});

export const {
  useGetIntegrationsQuery,
  useLazyGetIntegrationsQuery,
  useGetIntegrationDataQuery,
  useLazyGetIntegrationDataQuery,
  useUpdateIntegrationMutation,
  useDeleteIntegrationMutation,
  useCreateIntegrationLinkMutation,
  useRenewIntegrationLinkMutation,
  useGetIntegrationsConfigQuery,
  useUpdateIntegrationsConfigMutation,
} = integrationsApi;
